/**
 * 媒体存储服务
 * 
 * 负责媒体文件的上传和下载，整合了 OSS 和后端存储两种方式
 * 
 * 职责：
 * - 上传单个媒体文件到云端/服务器
 * - 上传项目所有媒体文件
 * - 提取项目中的媒体引用
 * - 管理已上传文件缓存
 */

import type { Project, Item } from '../../types/data'
import { uploadFileToOSS, generateObjectKey } from '../ossService'
import { getStorageConfig } from '../api'
import { 
  getMediaFromUserDB, 
  saveMediaToUserDB,
  getUploadedFileCacheFromUserDB,
  saveUploadedFileCacheToUserDB
} from '../dbManager'

// API 基础 URL
const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL
  }
  return '/api'
}

// ============== 工具函数 ==============

/**
 * 从 fileId 中提取原始 UUID（去掉扩展名）
 */
export function extractOriginalFileId(fileId: string): string {
  if (fileId && fileId.includes('.')) {
    return fileId.substring(0, fileId.lastIndexOf('.'))
  }
  return fileId
}

/**
 * 获取文件扩展名
 */
function getFileExtension(fileType?: string, ext?: string): string {
  let extension = ext || fileType?.split('/')[1] || 'png'
  // 处理 jpeg -> jpg 的映射
  if (extension === 'jpeg') extension = 'jpg'
  return extension
}

// ============== 上传缓存管理 ==============

/**
 * 检查文件是否已上传到云端
 */
export async function isFileUploaded(
  userUuid: string, 
  projectId: string, 
  fileId: string
): Promise<{ uploaded: boolean; storageProvider?: string } | false> {
  try {
    const originalFileId = extractOriginalFileId(fileId)
    const cacheKey = `${userUuid}:${projectId}:${originalFileId}`
    
    const result = await getUploadedFileCacheFromUserDB(userUuid, cacheKey)
    
    if (result) {
      return {
        uploaded: true,
        storageProvider: result.storageProvider || 'local'
      }
    }
    return false
  } catch (error) {
    console.error('检查文件是否已上传失败:', error)
    return false
  }
}

/**
 * 标记文件为已上传到云端
 */
export async function markFileAsUploaded(
  userUuid: string, 
  projectId: string, 
  fileId: string, 
  storageProvider: string = 'local'
): Promise<void> {
  try {
    const originalFileId = extractOriginalFileId(fileId)
    const cacheKey = `${userUuid}:${projectId}:${originalFileId}`
    
    await saveUploadedFileCacheToUserDB(userUuid, {
      cacheKey,
      userUuid,
      projectId,
      fileId: originalFileId,
      storageProvider,
      uploadedAt: new Date().toISOString()
    })
    
    console.log(`📦 文件已标记为已上传: ${originalFileId} (${storageProvider})`)
  } catch (error) {
    console.error('标记文件为已上传失败:', error)
  }
}

// ============== 媒体引用提取 ==============

/**
 * 提取单个项目中的所有媒体文件引用
 */
export function extractProjectMediaReferences(project: Project): string[] {
  const mediaRefs = new Set<string>()
  
  // 提取项目封面图引用
  if (project.coverImage && !project.coverImage.startsWith('data:image/')) {
    const coverFileId = extractOriginalFileId(project.coverImage)
    mediaRefs.add(coverFileId)
  }
  
  if (project.coverThumbnailFileId) {
    const thumbnailFileId = extractOriginalFileId(project.coverThumbnailFileId)
    mediaRefs.add(thumbnailFileId)
  }
  
  // 提取 items 中的媒体文件
  const allItems: Item[] = [
    ...(project.items || []),
    ...(project.groups?.flatMap(g => g.items) || [])
  ]
  
  for (const item of allItems) {
    // 图片类型
    if (item.type === 'image') {
      const fileId = item.fileId || (item as any).imageId // 兼容旧数据
      if (fileId) {
        mediaRefs.add(extractOriginalFileId(fileId))
      }
      // 缩略图
      if (item.thumbnailFileId) {
        mediaRefs.add(extractOriginalFileId(item.thumbnailFileId))
      }
    }
    
    // 文件类型
    if (item.type === 'file' && item.fileId) {
      mediaRefs.add(extractOriginalFileId(item.fileId))
    }
    
    // 文本中嵌入的图片
    if (item.type === 'text' && item.content) {
      const parser = new DOMParser()
      const doc = parser.parseFromString(item.content, 'text/html')
      const images = doc.querySelectorAll('img[src^="indexeddb://"]')
      images.forEach(img => {
        let fileId = img.getAttribute('src')?.replace('indexeddb://', '') || ''
        if (fileId) {
          mediaRefs.add(extractOriginalFileId(fileId))
        }
      })
    }
  }
  
  return Array.from(mediaRefs)
}

/**
 * 提取多个项目中的所有媒体文件引用
 */
export function extractMediaReferences(projects: Project[]): string[] {
  const allRefs = new Set<string>()
  
  for (const project of projects) {
    const refs = extractProjectMediaReferences(project)
    refs.forEach(ref => allRefs.add(ref))
  }
  
  return Array.from(allRefs)
}

// ============== 媒体上传 ==============

export interface UploadMediaResult {
  success: boolean
  serverFileId?: string  // 上传后的完整文件名（包含扩展名）
  error?: string
}

/**
 * 上传单个媒体文件到服务器/云端
 * 
 * @param userUuid 用户 UUID
 * @param projectId 项目 ID（UUID）
 * @param fileId 文件 ID（可以带扩展名也可以不带）
 * @returns 上传结果，包含服务器端的文件 ID
 */
export async function uploadMediaToServer(
  userUuid: string,
  projectId: string,
  fileId: string
): Promise<UploadMediaResult> {
  try {
    if (!userUuid) {
      return { success: false, error: '缺少用户UUID' }
    }
    
    if (!projectId) {
      return { success: false, error: '缺少项目ID' }
    }
    
    const originalFileId = extractOriginalFileId(fileId)
    
    // 从 IndexedDB 获取媒体文件
    const mediaData = await getMediaFromUserDB(userUuid, originalFileId)
    if (!mediaData || !mediaData.blob) {
      console.warn(`📦 [上传媒体] 本地未找到媒体文件: ${originalFileId}`)
      return { success: false, error: '本地未找到媒体文件' }
    }
    
    // 获取文件扩展名
    const ext = getFileExtension(mediaData.fileType, mediaData.ext)
    const fileName = `${originalFileId}.${ext}`
    
    // 检查文件是否已上传
    const uploadInfo = await isFileUploaded(userUuid, projectId, originalFileId)
    if (uploadInfo && uploadInfo.uploaded) {
      console.log(`📦 [上传媒体] 文件已上传，跳过: ${originalFileId}`)
      return { success: true, serverFileId: fileName }
    }
    
    // 获取存储配置
    const storageConfig = await getStorageConfig()
    const useOSS = storageConfig?.useOSS
    const storageProvider = storageConfig?.provider || 'local'
    
    console.log('📦 [上传媒体] 存储配置:', storageConfig)
    console.log('📦 [上传媒体] 是否使用OSS:', useOSS)
    
    // 如果使用 OSS，直接上传到 OSS
    if (useOSS) {
      try {
        const objectKey = generateObjectKey(userUuid, projectId, fileName)
        console.log('☁️ 开始上传媒体到OSS，objectKey:', objectKey)
        
        const uploadResult = await uploadFileToOSS(mediaData.blob, objectKey)
        console.log('☁️ OSS上传结果:', uploadResult)
        
        if (uploadResult) {
          // 上传成功，标记为已上传
          await markFileAsUploaded(userUuid, projectId, originalFileId, storageProvider)
          return { success: true, serverFileId: fileName }
        }
      } catch (ossError) {
        console.error('OSS上传失败，降级到后端上传:', ossError)
        // 降级到后端上传
      }
    }
    
    // 使用后端上传（本地存储或 OSS 失败时的降级方案）
    const formData = new FormData()
    formData.append('file', mediaData.blob, fileName)
    
    const apiBaseUrl = getApiBaseUrl()
    const response = await fetch(
      `${apiBaseUrl}/files/upload?userUuid=${userUuid}&projectId=${projectId}&fileId=${originalFileId}`,
      {
        method: 'POST',
        body: formData
      }
    )
    
    if (!response.ok) {
      throw new Error(`上传媒体失败: ${response.status}`)
    }
    
    const result = await response.json()
    if (result.success) {
      // 上传成功，标记为已上传
      await markFileAsUploaded(userUuid, projectId, originalFileId, 'local')
      
      // 返回完整文件名
      const serverFileId = result.data?.fileId || fileName
      return { 
        success: true, 
        serverFileId: serverFileId.includes('.') ? serverFileId : fileName 
      }
    }
    
    return { success: false, error: result.message || '上传失败' }
  } catch (error) {
    console.error(`上传媒体 ${fileId} 失败:`, error)
    return { success: false, error: (error as Error).message }
  }
}

export interface UploadProjectMediaResult {
  success: number
  failed: number
  skipped: number
  mediaMapping: Map<string, string>  // 原始 fileId -> 服务器 fileId
}

/**
 * 上传项目的所有媒体文件到服务器/云端
 * 
 * @param userUuid 用户 UUID
 * @param project 项目数据
 * @param onProgress 进度回调
 * @returns 上传结果统计和文件映射
 */
export async function uploadProjectMediaToServer(
  userUuid: string,
  project: Project,
  onProgress?: (current: number, total: number, fileId: string) => void
): Promise<UploadProjectMediaResult> {
  const projectId = project.uuid || project.id
  if (!projectId) {
    throw new Error('项目缺少 UUID')
  }
  
  // 提取所有媒体引用
  const mediaRefs = extractProjectMediaReferences(project)
  console.log(`📦 [uploadProjectMedia] 提取到 ${mediaRefs.length} 个媒体文件引用:`, mediaRefs)
  
  let success = 0
  let failed = 0
  let skipped = 0
  const mediaMapping = new Map<string, string>()
  
  for (let i = 0; i < mediaRefs.length; i++) {
    const fileId = mediaRefs[i]
    onProgress?.(i + 1, mediaRefs.length, fileId)
    
    try {
      const result = await uploadMediaToServer(userUuid, projectId, fileId)
      
      if (result.success && result.serverFileId) {
        mediaMapping.set(fileId, result.serverFileId)
        success++
      } else if (result.error === '本地未找到媒体文件') {
        // 文件不存在，可能是已删除的引用
        skipped++
      } else {
        failed++
      }
    } catch (error) {
      console.error(`上传媒体 ${fileId} 失败:`, error)
      failed++
    }
  }
  
  return { success, failed, skipped, mediaMapping }
}

/**
 * 替换项目数据中的媒体文件引用
 * 
 * @param project 项目数据（会被修改）
 * @param mediaMapping 文件映射（原始 fileId -> 服务器 fileId）
 * @returns 替换的数量
 */
export function replaceProjectMediaReferences(
  project: Project,
  mediaMapping: Map<string, string>
): number {
  if (mediaMapping.size === 0) return 0
  
  let replaceCount = 0
  
  // 替换封面图引用
  if (project.coverImage && !project.coverImage.startsWith('data:image/')) {
    const originalId = extractOriginalFileId(project.coverImage)
    if (mediaMapping.has(originalId)) {
      project.coverImage = mediaMapping.get(originalId)!
      replaceCount++
    }
  }
  
  if (project.coverThumbnailFileId) {
    const originalId = extractOriginalFileId(project.coverThumbnailFileId)
    if (mediaMapping.has(originalId)) {
      project.coverThumbnailFileId = mediaMapping.get(originalId)!
      replaceCount++
    }
  }
  
  // 替换 items 中的媒体文件引用
  if (project.items) {
    for (const item of project.items) {
      // 替换 image 和 file 类型的 fileId
      const currentFileId = item.fileId || (item as any).imageId
      if (currentFileId) {
        const originalId = extractOriginalFileId(currentFileId)
        if (mediaMapping.has(originalId)) {
          item.fileId = mediaMapping.get(originalId)!
          // 兼容旧数据
          if ((item as any).imageId) {
            (item as any).imageId = item.fileId
          }
          replaceCount++
        }
      }
      
      // 替换缩略图
      if (item.thumbnailFileId) {
        const originalId = extractOriginalFileId(item.thumbnailFileId)
        if (mediaMapping.has(originalId)) {
          item.thumbnailFileId = mediaMapping.get(originalId)!
          replaceCount++
        }
      }
      
      // 替换文本中嵌入的图片引用
      if (item.type === 'text' && item.content) {
        let content = item.content
        let contentChanged = false
        
        mediaMapping.forEach((serverId, localId) => {
          const oldContent = content
          // 替换 indexeddb:// 引用
          content = content.replace(
            new RegExp(`indexeddb://${localId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:\\.\\w+)?`, 'g'),
            `indexeddb://${serverId}`
          )
          if (content !== oldContent) {
            contentChanged = true
          }
        })
        
        if (contentChanged) {
          item.content = content
          replaceCount++
        }
      }
    }
  }
  
  console.log(`📦 [replaceMediaRefs] 共替换了 ${replaceCount} 个文件引用`)
  return replaceCount
}

// ============== 导出下载功能（从 mediaDownloader 重新导出） ==============

export { downloadMediaFilesFromServer } from '../mediaDownloader'
